import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm, FormProvider, useFieldArray } from 'react-hook-form'
import { useNavigate, generatePath } from 'react-router-dom'

import PropTypes from 'prop-types'
import produce from 'immer'

import {
  Button,
  Divider,
  Form as AntForm,
  Col,
  notification,
  Row,
  Switch,
  Select,
  Typography,
  InputNumber
} from 'antd'
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { colours } from '@qflow/theme'

import * as ROUTES from 'admin/routes'
import { useApi } from 'admin/lib/Api'
import { Section, SectionProvider, FormItem } from 'admin/lib/Forms'
import { COUNTRY, MODULE_ID, SUBSCRIPTION_TIER } from '@qflow/constants'

import { SiteAddressInput } from './SiteAddressInput'
import { DisplayProjectUpdateErrorModal } from './ProjectUpdateErrorModal'

const DEFAULT_ADDRESS = {
  address: '',
  displayName: '',
  lon: '',
  lat: ''
}

export function EditProject({
  project,
  isNew,
  accountId,
  projectId,
  projectAlertRulesEnabledCount,
  rawTimeZoneOptions
}) {
  const navigate = useNavigate()
  const [saveDisabled, setSaveDisabled] = useState(false)
  const [saveDisabledMissingAddress, setSaveDisabledMissingAddress] =
    useState(false)

  const [timeZoneOptions, setTimeZoneOptions] = useState([])
  const [showMissingAddressWarning, setShowMissingAddressWarning] =
    useState(false)
  const [showSiteAddressBehaviourWarning, setShowSiteAddressBehaviourWarning] =
    useState(false)
  const [showZeroMaxAlertRulesWarning, setShowZeroMaxAlertRulesWarning] =
    useState(false)

  const numberOfEnabledAlertRules = useMemo(() => {
    if (isNew || !projectAlertRulesEnabledCount) {
      return 0
    }
    return projectAlertRulesEnabledCount || 0
  }, [isNew, projectAlertRulesEnabledCount])

  const formattedProject = { ...project }

  if (formattedProject && formattedProject.siteAddresses) {
    formattedProject.siteAddresses.forEach((siteAddress, i) => {
      if (siteAddress.addressCoordinates) {
        if (siteAddress.addressCoordinates.lat) {
          formattedProject.siteAddresses[i].lat =
            siteAddress.addressCoordinates.lat
        }
        if (siteAddress.addressCoordinates.lon) {
          formattedProject.siteAddresses[i].lon =
            siteAddress.addressCoordinates.lon
        }
      }
      delete formattedProject.siteAddresses[i].addressCoordinates
    })
  }

  //
  // Form setup & values
  const draftItem = isNew ? blankProject : calculateProject(project)

  const form = useForm({
    defaultValues: draftItem,
    mode: 'all',
    criteriaMode: 'firstError'
  })

  const siteAddressFields = useFieldArray({
    control: form.control,
    name: 'siteAddresses'
  })

  const currentCountry = form.watch('country')
  const currentMaxAlertRules = form.watch('maxAlertRules')
  const currentSubscriptionTier = form.watch('subscriptionTier')

  useEffect(() => {
    // Auto-set maxAlertRules to 0 when subscription tier changes to Capture
    if (currentSubscriptionTier === SUBSCRIPTION_TIER.CAPTURE) {
      form.setValue('maxAlertRules', 0)
    }
  }, [currentSubscriptionTier, form])

  useEffect(() => {
    // Auto-set isAlertsEnabled based on maxAlertRules value
    if (currentMaxAlertRules === 0) {
      form.setValue('isAlertsEnabled', false)
    } else if (currentMaxAlertRules > 0) {
      form.setValue('isAlertsEnabled', true)
    }
  }, [currentMaxAlertRules, form])

  useEffect(() => {
    // Only show warning if maxAlertRules is 0 and subscription tier is NOT Capture
    if (
      currentMaxAlertRules === 0 &&
      currentSubscriptionTier !== SUBSCRIPTION_TIER.CAPTURE
    ) {
      setShowZeroMaxAlertRulesWarning(true)
    } else {
      setShowZeroMaxAlertRulesWarning(false)
    }
  }, [currentMaxAlertRules, currentSubscriptionTier])

  //
  // Callbacks

  const api = useApi()

  const onSubmit = useCallback(
    async data => {
      setSaveDisabled(true)

      const currentProject = project
      const submissionData = transformFormDataToProject(currentProject, data)

      const createNewProject = async (accountId, submissionData) => {
        const [responseData, status, error] = await api.registerProject(
          accountId,
          data.name
        )
        if (error) {
          return [responseData, status, error]
        }

        return await api.updateProject(
          accountId,
          responseData.projectId,
          submissionData
        )
      }

      const [, status, error] = isNew
        ? await createNewProject(accountId, submissionData)
        : await api.updateProject(accountId, projectId, submissionData)

      if (error) {
        console.log(submissionData)
        DisplayProjectUpdateErrorModal(error, status)
        setSaveDisabled(false)
      } else {
        setSaveDisabled(false)
        navigate(
          generatePath(ROUTES.PROJECTS.LIST, {
            accountId: accountId
          })
        )
        notification.success({
          message: 'Successfully saved project'
        })
      }
    },
    [accountId, api, isNew, navigate, project, projectId]
  )

  const handleSubmit = form.handleSubmit(onSubmit)

  const countryOptions = useMemo(
    () => [
      { value: COUNTRY.AUSTRALIA, label: COUNTRY.AUSTRALIA },
      { value: COUNTRY.CANADA, label: COUNTRY.CANADA },
      { value: COUNTRY.FRANCE, label: COUNTRY.FRANCE },
      { value: COUNTRY.GERMANY, label: COUNTRY.GERMANY },
      { value: COUNTRY.IRELAND, label: COUNTRY.IRELAND },
      { value: COUNTRY.SWITZERLAND, label: COUNTRY.SWITZERLAND },
      { value: COUNTRY.UK, label: COUNTRY.UK },
      { value: COUNTRY.USA, label: COUNTRY.USA }
    ],
    []
  )
  const subscriptionTierOptions = useMemo(
    () => [
      { value: SUBSCRIPTION_TIER.LEGACY, label: SUBSCRIPTION_TIER.LEGACY },
      { value: SUBSCRIPTION_TIER.CAPTURE, label: SUBSCRIPTION_TIER.CAPTURE },
      { value: SUBSCRIPTION_TIER.COMPLY, label: SUBSCRIPTION_TIER.COMPLY },
      { value: SUBSCRIPTION_TIER.CONSULT, label: SUBSCRIPTION_TIER.CONSULT },
      { value: SUBSCRIPTION_TIER.PIONEERS, label: SUBSCRIPTION_TIER.PIONEERS }
    ],
    []
  )

  const siteAddressBehaviourOptions = useMemo(
    () => [
      { value: 'Off', label: 'Off' },
      {
        value: 'GapFill',
        label:
          'Gap Fill: Use only when new tickets do not have Waste Origin or Delivery Destination addresses.'
      },
      {
        value: 'Force',
        label:
          'Force: Use as Waste Origin or Delivery Destination for all new tickets.'
      }
    ],
    []
  )

  useEffect(() => {
    const timeZoneOptionKeyMap = { displayName: 'label', id: 'value' }
    const tempTimeZoneOptions = []

    if (rawTimeZoneOptions && rawTimeZoneOptions.length > 0) {
      rawTimeZoneOptions.forEach(timeZone => {
        const updatedItem = Object.keys(timeZone).map(key => {
          const newKey = timeZoneOptionKeyMap[key]
          return { [newKey]: timeZone[key] }
        })
        updatedItem.push({ title: timeZone.id })
        tempTimeZoneOptions.push(
          updatedItem.reduce((a, b) => Object.assign({}, a, b))
        )
      })
    }
    setTimeZoneOptions(tempTimeZoneOptions)
  }, [rawTimeZoneOptions])

  const checkForPopulatedAddress = () => {
    setTimeout(() => {
      const currentFormData = form.getValues()
      let addressCount = 0
      if (
        currentFormData &&
        currentFormData.siteAddresses &&
        currentFormData.siteAddresses.length > 0
      ) {
        currentFormData.siteAddresses.forEach(siteAddress => {
          if (siteAddress.address) {
            addressCount++
          }
        })

        setSaveDisabledMissingAddress(
          addressCount < currentFormData.siteAddresses.length
        )
        setShowMissingAddressWarning(
          addressCount < currentFormData.siteAddresses.length
        )
      } else {
        setSaveDisabledMissingAddress(false)
        setShowMissingAddressWarning(false)
      }
    }, 100)
  }

  const checkSiteAddressBehaviourConditions = () => {
    setTimeout(() => {
      const currentFormData = form.getValues()
      if (
        currentFormData &&
        currentFormData.siteAddressBehaviour !==
          siteAddressBehaviourOptions[0].value
      ) {
        if (
          currentFormData.siteAddresses &&
          currentFormData.siteAddresses.length === 1 &&
          currentFormData.siteAddresses[0].address
        ) {
          setShowSiteAddressBehaviourWarning(false)
        } else {
          setShowSiteAddressBehaviourWarning(true)
        }
      } else {
        setShowSiteAddressBehaviourWarning(false)
      }
    }, 100)
  }

  const handleAddSiteAddress = () => {
    siteAddressFields.append(DEFAULT_ADDRESS, true)
    checkSiteAddressBehaviourConditions()
  }

  const handleRemoveSiteAddress = i => {
    siteAddressFields.remove(i)
    checkSiteAddressBehaviourConditions()
  }

  return (
    <FormProvider {...form}>
      <SectionProvider>
        <Col style={{ paddingTop: '1rem', paddingBottom: '2rem' }}>
          <AntForm
            onFinish={handleSubmit}
            layout="vertical"
            style={{ maxWidth: 1000 }}
          >
            <Section>
              {({ firstInputRef }) => (
                <>
                  <Typography.Title level={3}>
                    {isNew ? 'Create New Project' : 'Edit Project'}
                  </Typography.Title>
                  <Row>
                    <FormItem
                      name="name"
                      label="Project Name"
                      inputRef={firstInputRef}
                      rules={{
                        minLength: 3,
                        required: true
                      }}
                      style={{ width: '100%' }}
                    />
                  </Row>

                  <Row>
                    <FormItem
                      name="country"
                      label="Country"
                      defaultValue={countryOptions[6].value}
                      style={{ width: '100%' }}
                    >
                      <Select
                        options={countryOptions}
                        style={{ width: '100%' }}
                      />
                    </FormItem>
                  </Row>
                  <Row>
                    <FormItem
                      name="timeZone"
                      label="Time Zone"
                      style={{ width: '100%' }}
                    >
                      <Select
                        options={timeZoneOptions}
                        showSearch
                        filterOption={(input, option) =>
                          option.label
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0 ||
                          option.value
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                        style={{ width: '100%' }}
                      />
                    </FormItem>
                  </Row>
                  <Divider />

                  <Row align="middle">
                    <Col span={5}>
                      <Typography.Title level={5}>
                        Subscription Tier
                      </Typography.Title>
                    </Col>
                    <Col span={3}>
                      <FormItem
                        name="subscriptionTier"
                        defaultValue={subscriptionTierOptions[0].value}
                        style={{ width: '100%', display: 'flex' }}
                      >
                        <Select
                          options={subscriptionTierOptions}
                          style={{ width: '100%' }}
                        />
                      </FormItem>
                    </Col>
                  </Row>
                  <Divider
                    style={{
                      margin: '0.25rem 0 0.5rem',
                      borderColor: colours.CONTROL_PANEL_GREY
                    }}
                  />

                  <FormItem name="isAlertsEnabled" hidden defaultValue={true} />

                  <Row align="middle">
                    <Col span={5}>
                      <Typography.Title
                        level={5}
                        style={{ paddingRight: '5px' }}
                      >
                        Max Alert Rules
                      </Typography.Title>
                    </Col>
                    <Col span={3}>
                      <FormItem
                        name="maxAlertRules"
                        style={{ width: '100%', display: 'flex' }}
                        defaultValue={draftItem.maxAlertRules}
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          max={255}
                        />
                      </FormItem>
                    </Col>
                    <Col>
                      <Typography
                        style={{ paddingLeft: '5px', fontStyle: 'italic' }}
                      >
                        Set to 255 for Infinite
                      </Typography>
                    </Col>
                    <Col>
                      <Typography
                        style={{
                          paddingLeft: '10px',
                          fontWeight:
                            currentMaxAlertRules < numberOfEnabledAlertRules
                              ? 'bold'
                              : 'normal',
                          color:
                            currentMaxAlertRules < numberOfEnabledAlertRules
                              ? colours.ERROR
                              : 'inherit'
                        }}
                      >
                        (Currently enabled: {numberOfEnabledAlertRules})
                      </Typography>
                    </Col>
                  </Row>
                  <Divider
                    style={{
                      margin: '0.25rem 0 0.5rem',
                      borderColor: colours.CONTROL_PANEL_GREY
                    }}
                  />
                  <Row align="middle">
                    <Col span={5}>
                      <Typography.Title level={5}>
                        Extended Product Groups
                      </Typography.Title>
                    </Col>
                    <Col span={2}>
                      <FormItem
                        name="isExtendedProductGroupsEnabled"
                        style={{ width: '100%', display: 'flex' }}
                      >
                        <Switch
                          defaultChecked={
                            draftItem.isExtendedProductGroupsEnabled
                          }
                          checkedChildren="Yes"
                          unCheckedChildren="No"
                          style={{ marginBottom: '2px' }}
                        />
                      </FormItem>
                    </Col>
                    <Col span={4} offset={1}>
                      <Typography.Title level={5}>
                        Uniclass Codes
                      </Typography.Title>
                    </Col>
                    <Col span={2}>
                      <FormItem
                        name="isUniclassCodesEnabled"
                        style={{ width: '100%', display: 'flex' }}
                      >
                        <Switch
                          defaultChecked={draftItem.isUniclassCodesEnabled}
                          checkedChildren="Yes"
                          unCheckedChildren="No"
                          style={{ marginBottom: '2px' }}
                        />
                      </FormItem>
                    </Col>
                    <Col span={4} offset={1}>
                      <Typography.Title level={5}>
                        Design Matches
                      </Typography.Title>
                    </Col>
                    <Col span={2}>
                      <FormItem
                        name="isDesignMatchesEnabled"
                        style={{ width: '100%', display: 'flex' }}
                      >
                        <Switch
                          defaultChecked={draftItem.isDesignMatchesEnabled}
                          checkedChildren="Yes"
                          unCheckedChildren="No"
                          style={{ marginBottom: '2px' }}
                        />
                      </FormItem>
                    </Col>
                  </Row>
                  <Row align="middle">
                    <Col span={8}>
                      Raise a service request for enabling extended product
                      groups if account not already{' '}
                      <a
                        href="https://dev.azure.com/qualisflow/Platform/_git/terraform?path=/prod/env.tfvars&version=GBdev/main&line=103&lineEnd=104&lineStartColumn=1&lineEndColumn=1&lineStyle=plain&_a=contents"
                        target="_blank"
                        rel="noreferrer"
                      >
                        listed
                      </a>
                    </Col>
                  </Row>
                  <Divider />

                  <Row align="middle">
                    <Col span={7}>
                      <Typography.Title level={5}>
                        3rd Party Integrations
                      </Typography.Title>
                    </Col>
                  </Row>
                  <Divider
                    style={{
                      margin: '0.25rem 0 0.5rem',
                      borderColor: colours.CONTROL_PANEL_GREY
                    }}
                  />
                  <Row align="middle">
                    <Col span={5}>
                      <Typography.Title level={5}>Autodesk</Typography.Title>
                    </Col>
                    <Col span={2}>
                      <FormItem
                        name="isAutodeskEnabled"
                        style={{ width: '100%', display: 'flex' }}
                      >
                        <Switch
                          defaultChecked={draftItem.isAutodeskEnabled}
                          checkedChildren="Yes"
                          unCheckedChildren="No"
                          style={{ marginBottom: '2px' }}
                        />
                      </FormItem>
                    </Col>
                  </Row>

                  <Divider />

                  {draftItem.moduleTags.map((tag, index) => {
                    const field = name => `moduleTags[${index}].${name}`

                    return (
                      <div key={index}>
                        <Divider
                          style={{
                            margin: '0.25rem 0 0.5rem',
                            borderColor: colours.CONTROL_PANEL_GREY
                          }}
                        />
                        <Row align="middle">
                          <Col span={3}>
                            <FormItem
                              name={field('moduleTagDefinitionId')}
                              hidden={true}
                            />
                            <Typography.Title level={5}>
                              {
                                draftItem.moduleTags[index]
                                  .moduleTagDefinitionName
                              }
                            </Typography.Title>
                          </Col>
                          <Col span={2}>
                            <FormItem
                              name={field('isTagged')}
                              style={{ paddingRight: '1em', display: 'flex' }}
                            >
                              <Switch
                                checkedChildren="Yes"
                                unCheckedChildren="No"
                                defaultChecked={
                                  draftItem.moduleTags[index].isTagged
                                }
                                style={{ marginBottom: '2px' }}
                              />
                            </FormItem>
                          </Col>
                          <Col span={2}>
                            <Typography.Title
                              level={5}
                              style={{ paddingRight: '5px' }}
                            >
                              Wiki URL
                            </Typography.Title>
                          </Col>
                          <Col span={17}>
                            <FormItem
                              name={field('wikiURI')}
                              style={{ width: '100%', display: 'flex' }}
                            />
                          </Col>
                        </Row>
                      </div>
                    )
                  })}

                  <Divider />
                  <Typography.Title level={5}>
                    Site Address Details
                  </Typography.Title>

                  {isNew && (
                    <p style={{ fontStyle: 'italic' }}>
                      Site Addresses can be configured once you have saved the
                      new Project.
                    </p>
                  )}

                  {!isNew && (
                    <>
                      <FormItem
                        name="siteAddressBehaviour"
                        label="Site Address Behaviour"
                        defaultValue={siteAddressBehaviourOptions[0].value}
                        style={{ width: '100%' }}
                      >
                        <Select
                          options={siteAddressBehaviourOptions}
                          onSelect={checkSiteAddressBehaviourConditions}
                          style={{ width: '100%' }}
                        />
                      </FormItem>

                      {showSiteAddressBehaviourWarning && (
                        <p className="warning">
                          To enable the automatic Site Address behaviours you
                          must have a single valid Site.
                        </p>
                      )}

                      <div style={{ marginBottom: '20px' }} />

                      {siteAddressFields.fields.map((siteAddress, i) => {
                        const field = name => `siteAddresses[${i}].${name}`
                        const getProps = name => ({
                          name: field(name),
                          defaultValue: siteAddress[name]
                        })
                        const keyPart = getProps('addressId').defaultValue
                          ? getProps('addressId').defaultValue
                          : 'blank'
                        const key = `siteAddress-${keyPart}-${i}`

                        return (
                          <Section key={key}>
                            {() => (
                              <>
                                <Row justify="space-between">
                                  <Typography.Title level={5}>
                                    Site {i + 1}
                                  </Typography.Title>

                                  <Button
                                    type="ghost"
                                    danger
                                    onClick={() => handleRemoveSiteAddress(i)}
                                    icon={<MinusCircleOutlined />}
                                    tabIndex="-1"
                                  />
                                </Row>

                                <SiteAddressInput
                                  form={form}
                                  label={'Address'}
                                  currentValue={
                                    getProps('address').defaultValue
                                  }
                                  addressPath={getProps('address').name}
                                  latPath={getProps('lat').name}
                                  lonPath={getProps('lon').name}
                                  checkForPopulatedAddress={() => {
                                    checkForPopulatedAddress()
                                    checkSiteAddressBehaviourConditions()
                                  }}
                                  projectCountry={currentCountry}
                                />

                                <FormItem
                                  {...getProps('displayName')}
                                  label="Display name"
                                />

                                <FormItem hidden {...getProps('lat')} />
                                <FormItem hidden {...getProps('lon')} />
                                <FormItem hidden {...getProps('addressId')} />
                              </>
                            )}
                          </Section>
                        )
                      })}
                      <Section>
                        {({ firstInputRef }) => (
                          <Button
                            type="dashed"
                            style={{ width: '100%' }}
                            onClick={handleAddSiteAddress}
                            icon={<PlusOutlined />}
                            ref={firstInputRef}
                          >
                            Add site details
                          </Button>
                        )}
                      </Section>
                    </>
                  )}
                </>
              )}
            </Section>

            <Col align="middle">
              {showMissingAddressWarning && (
                <p
                  className="warning"
                  style={{ display: 'inline-block', marginRight: '1rem' }}
                >
                  All sites must have an address set
                </p>
              )}

              {showZeroMaxAlertRulesWarning && (
                <p
                  className="warning"
                  style={{ display: 'inline-block', marginRight: '1rem' }}
                >
                  Max Alert Rules must be greater than 0 for non-Capture
                  subscription tiers.
                </p>
              )}

              {currentMaxAlertRules < numberOfEnabledAlertRules && (
                <p
                  className="warning"
                  style={{ display: 'inline-block', marginRight: '1rem' }}
                >
                  Max Alert Rules cannot be less than the number of currently
                  enabled Alert Rules
                </p>
              )}

              <Button
                type="primary"
                htmlType="submit"
                disabled={
                  saveDisabled ||
                  saveDisabledMissingAddress ||
                  showSiteAddressBehaviourWarning ||
                  showZeroMaxAlertRulesWarning ||
                  currentMaxAlertRules < numberOfEnabledAlertRules
                }
              >
                {isNew ? 'Create' : 'Update'}
              </Button>
            </Col>
          </AntForm>
        </Col>
      </SectionProvider>
    </FormProvider>
  )
}

const blankProject = {
  name: '',
  country: COUNTRY.UK,
  timeZone: 'Europe/London',
  moduleTags: [
    {
      moduleTagDefinitionId: MODULE_ID.WASTE,
      moduleTagDefinitionName: 'Waste',
      isTagged: false,
      wikiURI: null
    },
    {
      moduleTagDefinitionId: MODULE_ID.DELIVERY,
      moduleTagDefinitionName: 'Delivery',
      isTagged: false,
      wikiURI: null
    }
  ],
  siteAddresses: [],
  subscriptionTier: SUBSCRIPTION_TIER.COMPLY,
  isAlertsEnabled: true,
  maxAlertRules: 17
}

function calculateProject(project) {
  project.moduleTags.filter(
    m => m.moduleTagDefinitionId === MODULE_ID.WASTE
  )[0].wikiURI = project.wikiUrlWaste
  project.moduleTags.filter(
    m => m.moduleTagDefinitionId === MODULE_ID.DELIVERY
  )[0].wikiURI = project.wikiUrlDelivery
  return project
}

function transformFormDataToProject(currentProject, values) {
  return produce(currentProject, draft => {
    Object.assign(draft, values)

    const wasteModule = draft.moduleTags.filter(
      m => m.moduleTagDefinitionId === MODULE_ID.WASTE
    )[0]
    const deliveryModule = draft.moduleTags.filter(
      m => m.moduleTagDefinitionId === MODULE_ID.DELIVERY
    )[0]

    draft.moduleTags = [
      produce(wasteModule, module => {
        delete module.wikiURI
      }),
      produce(deliveryModule, module => {
        delete module.wikiURI
      })
    ]

    draft.wikiUrlWaste = wasteModule.wikiURI
    draft.wikiUrlDelivery = deliveryModule.wikiURI

    if (values.isExtendedProductGroupsEnabled === '') {
      draft.isExtendedProductGroupsEnabled = false
    }

    if (values.isUniclassCodesEnabled === '') {
      draft.isUniclassCodesEnabled = false
    }

    if (values.isDesignMatchesEnabled === '') {
      draft.isDesignMatchesEnabled = false
    }

    if (values.isProLicence === '') {
      draft.isProLicence = false
    }

    if (values.isAlertsEnabled === '') {
      draft.isAlertsEnabled = false
    }

    if (values.isAutodeskEnabled === '') {
      draft.isAutodeskEnabled = false
    }

    draft.siteAddresses = (values.siteAddresses ?? []).map(formAddress => {
      const currentAddress = (currentProject.siteAddresses ?? []).find(
        currentAddress => formAddress.addressId === currentAddress.addressId
      )

      if (formAddress.addressId === '') {
        delete formAddress.addressId
      }

      formAddress.addressCoordinates = {
        lat: formAddress.lat,
        lon: formAddress.lon
      }

      delete formAddress.lat
      delete formAddress.lon

      return produce(currentAddress ?? {}, draftAddress => {
        Object.assign(draftAddress, formAddress)
      })
    })
  })
}

EditProject.propTypes = {
  project: PropTypes.object,
  isNew: PropTypes.bool.isRequired,
  accountId: PropTypes.string.isRequired,
  projectId: PropTypes.string
}
